// Resource node rendering for ZoneDO nodes in world-space.
//
// Nodes are drawn as diamonds (rotated squares) so they are visually
// distinct from the square player sprites. Active nodes show their item
// color; depleted nodes are dimmed to signal unavailability.
//
// The layer is inserted into worldContainer before any player sprites
// are added, so nodes render under players automatically.

import { Container, Graphics } from "pixi.js";
import { balance } from "./types.ts";
import { items } from "../../shared/items.ts";
import type { ResourceNode } from "../../shared/messages.ts";

export class NodeRenderer {
  private readonly layer: Container;
  private readonly nodeGfx = new Map<string, Graphics>();

  constructor(worldContainer: Container) {
    this.layer = new Container();
    worldContainer.addChild(this.layer);
  }

  // Seed from the joined payload. Clears any prior nodes first so a
  // reconnect doesn't leave ghost graphics.
  init(nodes: ResourceNode[]): void {
    for (const gfx of this.nodeGfx.values()) gfx.destroy();
    this.nodeGfx.clear();
    for (const node of nodes) {
      this.addNode(node);
    }
  }

  setDepleted(nodeId: string): void {
    const gfx = this.nodeGfx.get(nodeId);
    if (!gfx) return;
    gfx.tint = 0x888888;
    gfx.alpha = balance.rendering.nodeDepletedAlpha;
  }

  setRespawned(nodeId: string): void {
    const gfx = this.nodeGfx.get(nodeId);
    if (!gfx) return;
    gfx.tint = 0xffffff;
    gfx.alpha = 1; // contracts:allow-literal — restore full opacity
  }

  private addNode(node: ResourceNode): void {
    const itemDef = items[node.itemId];
    const r = balance.rendering.nodeSizePx;
    // Diamond: four points at the cardinal offsets from center.
    const gfx = new Graphics()
      .poly([0, -r, r, 0, 0, r, -r, 0])
      .fill({ color: itemDef.color });
    gfx.x = node.x;
    gfx.y = node.y;
    if (node.respawnAt !== null) {
      gfx.tint = 0x888888;
      gfx.alpha = balance.rendering.nodeDepletedAlpha;
    }
    this.layer.addChild(gfx);
    this.nodeGfx.set(node.nodeId, gfx);
  }
}
